import React, { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import { Chip, Stack, View } from '@gogame/components';
import { SidePageV2, FormFieldGroup, withForm } from '@gogame/form';
import { usePages } from '@gogame/screens';

import {
  loadActions,
  loadAffiliates,
  loadApplications,
  loadCustomerCompanies,
  loadNetworks,
} from 'scenes/Users/AdminUsers/sources/fields';
import {
  UserPermissions,
  PermissionPickerCategory,
  PermissionPicker,
} from 'scenes/Users/components/UserPermissions';
import {
  CATEGORY_ACTIONS,
  CATEGORY_AFFILIATES,
  CATEGORY_APPLICATIONS,
  CATEGORY_CUSTOMER_COMPANIES,
  CATEGORY_NETWORKS,
  CATEGORY_PAGES,
} from 'scenes/Users/components/UserPermissions/constants';
import { PERMISSION_ACTION_SEE_ALL_AFFILIATES } from 'scenes/Users/AdminUsers/constants';
import { mapPageToOptions } from 'scenes/Users/utils';

import { PERMISSIONS } from 'scenes/Users/AdminUsers/components/UserForm/constants';
import { usePermissionChange } from 'scenes/Users/AdminUsers/hooks';

const defaultAvailableLabel = () => 'Available';
const defaultSelectedLabel = () => 'To add';

const BulkPermissionsPicker = ({
  availableLabel = defaultAvailableLabel,
  selectedLabel = defaultSelectedLabel,
  ...props
}) => (
  <PermissionPicker
    availableLabel={availableLabel}
    selectedLabel={selectedLabel}
    {...props}
  />
);

const PermissionsForm = withForm({})(({
  onClose,
  title,
  subTitle,
  loading,
  onExited,
  open,
  entityId,
  values,
  setFieldValue,
  selectedLabel,
}) => {
  const pages = usePages(mapPageToOptions, {
    allowDuplicatePermission: false,
  });

  const sortedPages = useMemo(() => {
    return orderBy(pages, ['name'], ['asc']);
  }, [pages]);

  const { isLoading, onCustomerCompaniesChange } = usePermissionChange({
    values,
    setFieldValue,
  });

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Stack
          direction={'row'}
          spacing={1}
          style={{ paddingBottom: 24 }}
          flexWrap={'wrap'}
        >
          {values.users?.map(({ name, id }) => (
            <Chip key={id} label={name} variant={'outlined'} />
          ))}
        </Stack>
        <View loading={isLoading}>
          <FormFieldGroup name={PERMISSIONS}>
            <UserPermissions>
              <PermissionPickerCategory
                id={CATEGORY_ACTIONS}
                name={CATEGORY_ACTIONS}
                label={'Actions'}
                source={loadActions}
              >
                <BulkPermissionsPicker selectedLabel={selectedLabel} />
              </PermissionPickerCategory>
              <PermissionPickerCategory
                id={CATEGORY_PAGES}
                name={CATEGORY_PAGES}
                label={'Pages'}
                options={sortedPages}
              >
                <BulkPermissionsPicker selectedLabel={selectedLabel} />
              </PermissionPickerCategory>
              <PermissionPickerCategory
                id={CATEGORY_CUSTOMER_COMPANIES}
                name={CATEGORY_CUSTOMER_COMPANIES}
                label={'Customer Companies'}
                source={loadCustomerCompanies}
              >
                <BulkPermissionsPicker
                  onChange={onCustomerCompaniesChange}
                  selectedLabel={selectedLabel}
                />
              </PermissionPickerCategory>
              <PermissionPickerCategory
                id={CATEGORY_AFFILIATES}
                name={CATEGORY_AFFILIATES}
                label={'Affiliates'}
                depends={[`${PERMISSIONS}.${CATEGORY_CUSTOMER_COMPANIES}`]}
                source={loadAffiliates}
                disabled={values[PERMISSIONS]?.[CATEGORY_ACTIONS]?.includes(
                  PERMISSION_ACTION_SEE_ALL_AFFILIATES
                )}
              >
                <BulkPermissionsPicker selectedLabel={selectedLabel} />
              </PermissionPickerCategory>
              <PermissionPickerCategory
                id={CATEGORY_NETWORKS}
                name={CATEGORY_NETWORKS}
                label={'Networks'}
                source={loadNetworks}
              >
                <BulkPermissionsPicker selectedLabel={selectedLabel} />
              </PermissionPickerCategory>
              <PermissionPickerCategory
                id={CATEGORY_APPLICATIONS}
                name={CATEGORY_APPLICATIONS}
                label={'Applications'}
                source={loadApplications}
              >
                <BulkPermissionsPicker selectedLabel={selectedLabel} />
              </PermissionPickerCategory>
            </UserPermissions>
          </FormFieldGroup>
        </View>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default PermissionsForm;
